import { VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Service } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { ScrollView as NBScrollView } from 'native-base';
import React from 'react';
import { Dimensions, StyleSheet } from 'react-native';
import { useRecoilValue } from 'recoil';

import { ScrollCloseToBottomProvider } from '../../contexts/ScrollCloseToBottom';
import { useAbsoluteRoute } from '../../hooks/useAbsoluteRoute';
import { useScrollCloseToBottom } from '../../hooks/useScrollCloseToBottom';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../routes/types';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../store/master/selector';
import {
  selectedTransactionFlowProduct,
  TransactionFlowProduct,
} from '../../store/transactionFlow/atom';
import { ProductTransactionFlow } from '../../store/transactionFlow/types';
import { FeatureToggle, useUnleashFlags } from '../../utils/useUnleashFlags';
import { CardProductItem } from '../TransactionFlow/components/CurrentPosition/CardProductItem';

import { DynamicAssetTable } from './components/DynamicAssetTable';
import { Header } from './components/Header';
import { getRenderFields } from './constants';

const blankProduct: TransactionFlowProduct = {
  product: {} as ProductTransactionFlow,
  data: null,
};

export const TransactionFlowDetails: React.FC<{
  defaultProduct?: TransactionFlowProduct;
  isInline?: boolean;
}> = ({ defaultProduct, isInline = false }) => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const { isCloseToBottom, validateIsCloseToBottom } = useScrollCloseToBottom();
  const currentRoute = useAbsoluteRoute();
  const selectedPartner = useRecoilValue(partnerIdToApisSelector);
  const selectedAdvisor = useRecoilValue(advisorIdToApisSelector);
  const { b2bShowModuleCustomers } = useUnleashFlags(FeatureToggle.Customers);
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'TransactionFlowDetails'>>();

  const products = useRecoilValue(selectedTransactionFlowProduct);
  let selectedProduct =
    params !== undefined ? products[params.product] || blankProduct : blankProduct;

  if (defaultProduct) {
    selectedProduct = defaultProduct;
  }

  const handleSelectClient = async (cpfCnpj: string) => {
    if (!cpfCnpj) {
      return;
    }

    const { data: customers } = await Service.Customer.V1.getByCpfCnpj({
      cpfCnpj,
      partnerId: selectedPartner,
      advisorId: selectedAdvisor,
      screenName: 'transaction-flow-search-historical',
    });

    if (!customers) {
      return;
    }

    const customer = customers[0].customer;

    if (b2bShowModuleCustomers) {
      const method =
        currentRoute.name === 'AngularFrame_CustomerDetail'
          ? navigation.replace
          : navigation.navigate;
      method('AppDrawer', {
        screen: 'AngularFrame_CustomerDetail',
        params: {
          state: 'app.posicaoOnline.posicao',
          DocumentoCliente: customer.cpfCnpjText,
          IdConta: customer.accountId,
        },
      });
    } else {
      navigation.navigate('AppDrawer', {
        screen: 'CustomerDetail',
        params: {
          customerId: customer.customerId,
          accountNumber: customer.accountNumber,
          accountId: customer.accountId,
          cpfCnpj: customer.cpfCnpjText,
        },
      });
    }
  };

  if (isInline) {
    return (
      <DynamicAssetTable
        product={selectedProduct.product}
        productData={selectedProduct.data?.details}
        onSelectClient={handleSelectClient}
      />
    );
  }

  return (
    <NBScrollView
      scrollEventThrottle={200}
      onScroll={({ nativeEvent }) => validateIsCloseToBottom(nativeEvent)}
      style={styles.view}
      overflowY={isMobile ? 'auto' : 'hidden'}>
      <ScrollCloseToBottomProvider isCloseToBottom={isCloseToBottom}>
        <VStack
          py={Spacing['inset.lg']}
          space={Spacing['inline.xxs']}
          px={{ base: Spacing['stack.xxxs'], sm: Spacing['inline.xxs'], lg: 0 }}
          style={styles.view}
          overflow="auto">
          <VStack
            alignSelf="center"
            space={Spacing['stack.xxxs']}
            width="full"
            maxWidth={{ base: '100%', sm: '95%' }}
            minWidth={0}>
            <Header isMobile={isMobile} />
            <CardProductItem
              label={selectedProduct.product.label}
              color={selectedProduct.product.color}
              renderFields={getRenderFields(selectedProduct.product.key, selectedProduct.data)}
              productData={selectedProduct.data}
              productDefinition={selectedProduct.product}
            />
            <DynamicAssetTable
              product={selectedProduct.product}
              onSelectClient={handleSelectClient}
              productData={selectedProduct.data?.details}
            />
          </VStack>
        </VStack>
      </ScrollCloseToBottomProvider>
    </NBScrollView>
  );
};

const styles = StyleSheet.create({
  view: {
    minHeight: Dimensions.get('window').height - 70,
    maxHeight: Dimensions.get('window').height - 70,
    backgroundColor: '#fff',
  },
});
